/**
 * @file DebugErrorBoundary.jsx
 * @description React Error Boundary that feeds caught render errors into
 *              the auto-debugger and shows a polished dev fallback UI.
 *              In production it re-throws cleanly.
 */

import React from 'react';
import { reportReactError } from './useAutoDebugger';

const IS_DEV = import.meta.env.DEV;

export class DebugErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null, errorInfo: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        reportReactError(error, errorInfo);
        this.setState({ errorInfo });
    }

    handleReset = () => {
        this.setState({ hasError: false, error: null, errorInfo: null });
    };

    render() {
        if (!this.state.hasError) return this.props.children;

        if (!IS_DEV) {
            // In production show a minimal message, not raw stack traces
            return (
                <div style={{ padding: '2rem', textAlign: 'center', fontFamily: 'system-ui' }}>
                    <h2>Something went wrong. Please refresh the page.</h2>
                </div>
            );
        }

        const { error, errorInfo } = this.state;

        return (
            <div style={{
                position: 'fixed', inset: 0, zIndex: 99999,
                background: '#0f172a', color: '#f8fafc',
                fontFamily: '"JetBrains Mono", "Fira Code", "Courier New", monospace',
                overflow: 'auto', padding: '2rem',
            }}>
                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
                    <div style={{
                        width: 48, height: 48, borderRadius: '12px',
                        background: 'linear-gradient(135deg, #ef4444, #b91c1c)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '1.5rem',
                    }}>💥</div>
                    <div>
                        <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.15em', color: '#ef4444', fontWeight: 700 }}>
                            React Render Error — Auto Debugger
                        </div>
                        <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#f1f5f9' }}>
                            {error?.name ?? 'Error'}: {error?.message ?? 'Unknown error'}
                        </div>
                    </div>
                </div>

                {/* Stack trace */}
                {error?.stack && (
                    <section style={{ marginBottom: '1.5rem' }}>
                        <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#94a3b8', marginBottom: '0.5rem' }}>
                            📍 Error Stack
                        </div>
                        <pre style={{
                            background: '#1e293b', borderRadius: '8px', padding: '1rem',
                            fontSize: '0.8rem', lineHeight: 1.6, overflowX: 'auto',
                            border: '1px solid #334155', color: '#fca5a5', margin: 0,
                        }}>
                            {error.stack}
                        </pre>
                    </section>
                )}

                {/* Component stack */}
                {errorInfo?.componentStack && (
                    <section style={{ marginBottom: '1.5rem' }}>
                        <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#94a3b8', marginBottom: '0.5rem' }}>
                            🔗 Component Stack
                        </div>
                        <pre style={{
                            background: '#1e293b', borderRadius: '8px', padding: '1rem',
                            fontSize: '0.78rem', lineHeight: 1.6, overflowX: 'auto',
                            border: '1px solid #334155', color: '#86efac', margin: 0,
                        }}>
                            {errorInfo.componentStack}
                        </pre>
                    </section>
                )}

                {/* Actions */}
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                    <button
                        onClick={this.handleReset}
                        style={{
                            padding: '0.6rem 1.4rem', borderRadius: '8px', border: 'none', cursor: 'pointer',
                            background: '#3b82f6', color: '#fff', fontWeight: 700, fontSize: '0.85rem',
                        }}
                    >
                        ↩ Try to Recover
                    </button>
                    <button
                        onClick={() => window.location.reload()}
                        style={{
                            padding: '0.6rem 1.4rem', borderRadius: '8px',
                            border: '1px solid #475569', cursor: 'pointer',
                            background: 'transparent', color: '#94a3b8', fontWeight: 600, fontSize: '0.85rem',
                        }}
                    >
                        🔄 Reload Page
                    </button>
                    <button
                        onClick={() => navigator.clipboard?.writeText(`${error?.stack}\n\n${errorInfo?.componentStack ?? ''}`)}
                        style={{
                            padding: '0.6rem 1.4rem', borderRadius: '8px',
                            border: '1px solid #475569', cursor: 'pointer',
                            background: 'transparent', color: '#94a3b8', fontWeight: 600, fontSize: '0.85rem',
                        }}
                    >
                        📋 Copy to Clipboard
                    </button>
                </div>
            </div>
        );
    }
}
